/**
 * useAuth.js
 *
 * Google Identity Services (GIS) token client hook.
 *
 * GIS gives us an OAuth2 access token in-browser without any backend redirect
 * flow. The token client pops a Google consent dialog on first call; subsequent
 * calls silently refresh if the token is still valid.
 *
 * Scopes requested:
 *   - spreadsheets      (read + write Sheets data)
 *   - script.external_request  (call Apps Script Execution API)
 *
 * Usage:
 *   const { isSignedIn, userEmail, getToken, signIn, signOut } = useAuth();
 *
 *   // Sign in / request token:
 *   await signIn();
 *
 *   // Get a fresh access token before any API call:
 *   const token = await getToken();
 *   fetch(url, { headers: { Authorization: `Bearer ${token}` } })
 *
 *   // Sign out:
 *   signOut();
 */

import { useState, useEffect, useRef, useCallback } from 'react';

const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;

const SCOPES = [
  'https://www.googleapis.com/auth/spreadsheets',
  'https://www.googleapis.com/auth/script.external_request',
  'https://www.googleapis.com/auth/userinfo.email',
  'https://www.googleapis.com/auth/userinfo.profile',
  // drive.file (not drive.readonly/full) — the app can only see/write files
  // IT creates via the Drive API (e.g. "Save to Drive" on a generated
  // invoice), never the user's existing Drive contents. Not in
  // REQUIRED_SCOPES below: declining it only disables Save-to-Drive, core
  // sign-in still works.
  'https://www.googleapis.com/auth/drive.file',
].join(' ');

// Scopes the app cannot function without. If the user grants sign-in but
// declines any of these (Google shows a checkbox per permission), Sheets/Apps
// Script calls fail with 403 "insufficient authentication scopes". We detect
// that here instead of silently caching a useless token.
const REQUIRED_SCOPES = [
  'https://www.googleapis.com/auth/spreadsheets',
  'https://www.googleapis.com/auth/script.external_request',
];

// Token is considered "near expiry" 3 minutes before it actually expires.
const EXPIRY_BUFFER_MS = 3 * 60 * 1000;

// Persist the session across page refreshes. GIS keeps tokens purely
// in-memory, so without this every refresh bounced the user back to the
// sign-in screen. Access tokens live ≤1h; once the stored one expires,
// getToken()'s silent refresh (prompt: '') takes over — a visible Google
// prompt reappears at most when the browser's Google session itself needs
// interaction, not on every refresh.
const STORAGE_KEY = 'salesdash_google_auth';

function _loadStoredAuth() {
  try {
    const s = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null');
    if (!s?.token || Date.now() >= (s.expiresAt ?? 0) - EXPIRY_BUFFER_MS) return null;
    return s;
  } catch {
    return null;
  }
}

function _saveStoredAuth(patch) {
  try {
    const cur = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}') ?? {};
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...cur, ...patch }));
  } catch { /* storage unavailable — session just won't survive refresh */ }
}

function _clearStoredAuth() {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}

export function useAuth() {
  // Rehydrate a still-valid session from the previous page load.
  const storedRef = useRef(undefined);
  if (storedRef.current === undefined) storedRef.current = _loadStoredAuth();

  const [isSignedIn, setIsSignedIn] = useState(Boolean(storedRef.current));
  const [userEmail, setUserEmail] = useState(storedRef.current?.email ?? null);
  const [userName, setUserName] = useState(storedRef.current?.name ?? null);
  const [userAvatar, setUserAvatar] = useState(storedRef.current?.avatar ?? null);

  // Stored token info: { token, expiresAt }
  const tokenRef = useRef(
    storedRef.current
      ? { token: storedRef.current.token, expiresAt: storedRef.current.expiresAt }
      : null
  );

  // GIS token client instance
  const tokenClientRef = useRef(null);

  // Promise resolution for in-flight token requests
  const pendingResolveRef = useRef(null);
  const pendingRejectRef = useRef(null);

  // Deduplicates concurrent getToken() calls — all callers share one GIS popup
  const pendingPromiseRef = useRef(null);

  // Resolves once the token client is ready; callers await this before using it
  const clientReadyRef = useRef(null);
  const clientReadyResolveRef = useRef(null);
  const clientReadyRejectRef = useRef(null);
  if (!clientReadyRef.current) {
    clientReadyRef.current = new Promise((res, rej) => {
      clientReadyResolveRef.current = res;
      clientReadyRejectRef.current = rej;
    });
  }

  // ---------------------------------------------------------------------------
  // Initialise the GIS token client once window.google is available
  // ---------------------------------------------------------------------------
  useEffect(() => {
    // GIS script loads async — poll until available (max ~5s)
    let attempts = 0;
    const interval = setInterval(() => {
      attempts++;
      if (window.google?.accounts?.oauth2) {
        clearInterval(interval);
        _initTokenClient();
        clientReadyResolveRef.current();
      } else if (attempts > 50) {
        clearInterval(interval);
        const err = new Error('[useAuth] Google Identity Services script did not load.');
        console.error(err.message);
        clientReadyRejectRef.current(err);
      }
    }, 100);
    return () => clearInterval(interval);
  }, []);

  function _initTokenClient() {
    tokenClientRef.current = window.google.accounts.oauth2.initTokenClient({
      client_id: CLIENT_ID,
      scope: SCOPES,
      callback: (response) => {
        if (response.error) {
          console.error('[useAuth] Token error:', response.error);
          if (pendingRejectRef.current) {
            pendingRejectRef.current(new Error(response.error));
            pendingRejectRef.current = null;
            pendingResolveRef.current = null;
          }
          return;
        }

        // Verify the user actually granted the scopes we need. Google's
        // granular consent lets users approve sign-in while declining
        // individual permissions; without this check we'd cache a token that
        // 403s on every Sheets call.
        const oauth2 = window.google?.accounts?.oauth2;
        if (
          oauth2?.hasGrantedAllScopes &&
          !oauth2.hasGrantedAllScopes(response, ...REQUIRED_SCOPES)
        ) {
          console.error('[useAuth] Missing required scopes:', response.scope);
          const scopeErr = new Error(
            'This app needs permission to access Google Sheets. Please sign in ' +
              'again and keep every permission box checked.'
          );
          if (pendingRejectRef.current) {
            pendingRejectRef.current(scopeErr);
            pendingRejectRef.current = null;
            pendingResolveRef.current = null;
          }
          // Do NOT cache the token or mark signed-in — it can't do anything.
          return;
        }

        const expiresAt = Date.now() + (response.expires_in - 180) * 1000;
        tokenRef.current = { token: response.access_token, expiresAt };
        _saveStoredAuth({ token: response.access_token, expiresAt });

        if (pendingResolveRef.current) {
          pendingResolveRef.current(response.access_token);
          pendingResolveRef.current = null;
          pendingRejectRef.current = null;
        }

        // Fetch basic user info on first sign-in
        if (!isSignedIn) {
          _fetchUserInfo(response.access_token);
        }

        setIsSignedIn(true);
      },
    });
  }

  async function _fetchUserInfo(accessToken) {
    try {
      const res = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setUserEmail(data.email ?? null);
      setUserName(data.name ?? null);
      setUserAvatar(data.picture ?? null);
      _saveStoredAuth({ email: data.email ?? null, name: data.name ?? null, avatar: data.picture ?? null });
    } catch (e) {
      console.warn('[useAuth] Could not fetch user info:', e);
    }
  }

  // ---------------------------------------------------------------------------
  // getToken — returns a valid access token, requesting one if needed
  // ---------------------------------------------------------------------------
  const getToken = useCallback(async () => {
    // Return cached token if it's still fresh
    if (tokenRef.current && Date.now() < tokenRef.current.expiresAt - EXPIRY_BUFFER_MS) {
      return tokenRef.current.token;
    }

    // Wait for GIS to finish loading before trying to use the client
    await clientReadyRef.current;

    // If a GIS popup is already in flight, share it — don't open a second one
    if (pendingPromiseRef.current) {
      return pendingPromiseRef.current;
    }

    pendingPromiseRef.current = new Promise((resolve, reject) => {
      pendingResolveRef.current = (token) => { pendingPromiseRef.current = null; resolve(token); };
      pendingRejectRef.current = (err)   => { pendingPromiseRef.current = null; reject(err); };
      // prompt: '' means silent if possible, dialog only if needed
      tokenClientRef.current.requestAccessToken({ prompt: '' });
    });

    return pendingPromiseRef.current;
  }, []);

  // ---------------------------------------------------------------------------
  // signIn — explicitly shows the account picker / consent dialog
  // ---------------------------------------------------------------------------
  const signIn = useCallback(async () => {
    await clientReadyRef.current;
    return new Promise((resolve, reject) => {
      pendingResolveRef.current = resolve;
      pendingRejectRef.current = reject;
      tokenClientRef.current.requestAccessToken({ prompt: 'consent' });
    });
  }, []);

  // ---------------------------------------------------------------------------
  // signOut — revokes token and resets state
  // ---------------------------------------------------------------------------
  const signOut = useCallback(() => {
    const token = tokenRef.current?.token;
    tokenRef.current = null;
    _clearStoredAuth();
    setIsSignedIn(false);
    setUserEmail(null);
    setUserName(null);
    setUserAvatar(null);

    if (token && window.google?.accounts?.oauth2) {
      window.google.accounts.oauth2.revoke(token, () => {
        console.log('[useAuth] Token revoked.');
      });
    }
  }, []);

  return {
    isSignedIn,
    userEmail,
    userName,
    userAvatar,
    getToken,
    signIn,
    signOut,
  };
}
